
import React, { useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text } from '@react-three/drei';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import * as THREE from 'three';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { HelpCircle, Info } from 'lucide-react';

interface Risk3DRadarProps {
  data: {
    community: number;
    ethics: number;
    funding: number;
    innovation: number;
  };
}

// Configuration for the risk dimensions
const riskConfig = {
  community: {
    label: 'Community Engagement',
    color: "#8B5CF6", // purple
    position: [1, 0, 0],
    description: "Measures how well the project connects with and serves its target audience."
  },
  ethics: {
    label: 'Ethical Alignment',
    color: "#10B981", // green
    position: [0, 0, 1],
    description: "Evaluates how well the project adheres to ethical principles and standards."
  },
  funding: {
    label: 'Funding Stability',
    color: "#F59E0B", // amber
    position: [-1, 0, 0],
    description: "Assesses the financial foundation and sustainability of the project."
  },
  innovation: {
    label: 'Innovation Factor',
    color: "#3B82F6", // blue
    position: [0, 0, -1],
    description: "Rates the project's originality and potential for disruption in its field."
  }
};

interface RiskPillarProps {
  position: number[];
  height: number;
  color: string;
  rotation?: number;
}

// Component to render a single risk pillar
const RiskPillar: React.FC<RiskPillarProps> = ({ position, height, color, rotation = 0 }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  
  useFrame(() => {
    if (meshRef.current) {
      meshRef.current.rotation.y += 0.003;
    }
  });

  return (
    <mesh 
      position={[position[0], height / 2, position[2]]} 
      ref={meshRef}
    >
      <boxGeometry args={[0.5, height, 0.5]} />
      <meshStandardMaterial color={color} transparent opacity={0.8} />
    </mesh>
  );
};

// Component for showing scale measurements in 3D
const ScaleIndicator: React.FC = () => {
  return (
    <>
      {/* Y-axis scale lines */}
      {[0.1, 0.2, 0.3].map((height) => (
        <group key={`scale-${height}`} position={[1.5, height, 0]}>
          <mesh>
            <boxGeometry args={[0.2, 0.01, 0.01]} />
            <meshBasicMaterial color="#333333" />
          </mesh>
          <Text position={[0.15, 0, 0]} scale={[0.1, 0.1, 0.1]} color="#333333">
            {`${(height * 10).toFixed(0)}`}
          </Text>
        </group>
      ))}
    </>
  );
};

interface Risk3DSceneProps {
  data: {
    community: number;
    ethics: number;
    funding: number;
    innovation: number;
  };
}

// Component to render the risk assessment in 3D
const Risk3DScene: React.FC<Risk3DSceneProps> = ({ data }) => {
  // Scale the data values for better visualization
  const scaleFactor = 3;

  return (
    <>
      {/* Floor grid with labels */}
      <gridHelper args={[6, 10]} position={[0, 0, 0]} />
      
      {/* Risk pillars */}
      <RiskPillar 
        position={riskConfig.community.position} 
        height={data.community * (scaleFactor / 10)} 
        color={riskConfig.community.color} 
      />
      <RiskPillar 
        position={riskConfig.ethics.position} 
        height={data.ethics * (scaleFactor / 10)} 
        color={riskConfig.ethics.color} 
      />
      <RiskPillar 
        position={riskConfig.funding.position} 
        height={data.funding * (scaleFactor / 10)} 
        color={riskConfig.funding.color} 
      />
      <RiskPillar 
        position={riskConfig.innovation.position} 
        height={data.innovation * (scaleFactor / 10)} 
        color={riskConfig.innovation.color} 
      />
      
      {/* Axis labels */}
      <mesh position={[1.5, 0, 0]}>
        <sphereGeometry args={[0.05]} />
        <meshStandardMaterial color="#8B5CF6" />
      </mesh>
      <Text position={[2, 0, 0]} scale={[0.2, 0.2, 0.2]} color="#8B5CF6">
        Community
      </Text>
      
      <mesh position={[0, 0, 1.5]}>
        <sphereGeometry args={[0.05]} />
        <meshStandardMaterial color="#10B981" />
      </mesh>
      <Text position={[0, 0, 2]} scale={[0.2, 0.2, 0.2]} color="#10B981">
        Ethics
      </Text>
      
      <mesh position={[-1.5, 0, 0]}>
        <sphereGeometry args={[0.05]} />
        <meshStandardMaterial color="#F59E0B" />
      </mesh>
      <Text position={[-2, 0, 0]} scale={[0.2, 0.2, 0.2]} color="#F59E0B">
        Funding
      </Text>
      
      <mesh position={[0, 0, -1.5]}>
        <sphereGeometry args={[0.05]} />
        <meshStandardMaterial color="#3B82F6" />
      </mesh>
      <Text position={[0, 0, -2]} scale={[0.2, 0.2, 0.2]} color="#3B82F6">
        Innovation
      </Text>
      
      {/* Scale indicators on Y axis */}
      <mesh position={[0, 0, 0]}>
        <cylinderGeometry args={[0.02, 0.02, 3, 16]} />
        <meshStandardMaterial color="#666666" />
      </mesh>
      
      {/* Y-axis labels for scale */}
      {[0, 0.1, 0.2, 0.3].map((height) => (
        <group key={`y-label-${height}`}>
          <mesh position={[-0.1, height, 0]}>
            <boxGeometry args={[0.1, 0.01, 0.01]} />
            <meshStandardMaterial color="#666666" />
          </mesh>
          <Text position={[-0.25, height, 0]} scale={[0.1, 0.1, 0.1]} color="#666666">
            {`${(height * 10).toFixed(0)}`}
          </Text>
        </group>
      ))}
      
      <Text position={[0, 1.7, 0]} scale={[0.2, 0.2, 0.2]} color="#333333">
        Score Scale (0-10)
      </Text>
      
      {/* Ambient light for overall illumination */}
      <ambientLight intensity={0.6} />
      
      {/* Directional light for shadows and depth */}
      <directionalLight position={[5, 5, 5]} intensity={0.8} />
      <directionalLight position={[-5, 5, -5]} intensity={0.4} />
    </>
  );
};

const Risk3DRadar: React.FC<Risk3DRadarProps> = ({ data }) => {
  // Ensure data values are within 0-10 range
  const safeData = {
    community: Math.max(0, Math.min(10, data.community)),
    ethics: Math.max(0, Math.min(10, data.ethics)),
    funding: Math.max(0, Math.min(10, data.funding)),
    innovation: Math.max(0, Math.min(10, data.innovation)),
  };

  const getScoreRating = (score: number): string => {
    if (score < 3.5) return "High Risk";
    if (score < 6.5) return "Moderate";
    if (score < 8.5) return "Good";
    return "Excellent";
  };
  
  const getRatingColor = (score: number): string => {
    if (score < 3.5) return "text-red-600";
    if (score < 6.5) return "text-amber-500";
    if (score < 8.5) return "text-emerald-600";
    return "text-green-600";
  };

  return (
    <div className="w-full">
      <div className="p-4 bg-white rounded-lg border border-gray-100 mb-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium text-gray-800 flex items-center">
            3D Risk Assessment Visualization
            <Popover>
              <PopoverTrigger asChild>
                <button className="ml-1 text-gray-400 hover:text-gray-600">
                  <Info className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-4">
                <h4 className="font-medium mb-2">Understanding the 3D Risk Assessment</h4>
                <p className="text-sm text-gray-600 mb-2">
                  This 3D visualization displays four key risk factors that influence your project's success:
                </p>
                <ul className="text-xs text-gray-600 space-y-1 mb-3">
                  <li><span className="font-medium">Pillar Height:</span> Represents your score (0-10)</li>
                  <li><span className="font-medium">Higher Pillars:</span> Better performance in that area</li>
                  <li><span className="font-medium">Lower Pillars:</span> Areas that need improvement</li>
                </ul>
                <p className="text-xs text-gray-600">
                  <span className="font-medium">Interact:</span> Click and drag to rotate the view. Use mouse wheel to zoom in/out.
                </p>
              </PopoverContent>
            </Popover>
          </h3>
        </div>

        <div className="h-[280px] w-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg overflow-hidden">
          <Canvas camera={{ position: [3, 3, 3], fov: 50 }}>
            <Risk3DScene data={safeData} />
            <OrbitControls 
              enableZoom={true}
              enablePan={true}
              enableRotate={true}
              minDistance={2}
              maxDistance={8}
            />
          </Canvas>
        </div>
        
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Object.entries(riskConfig).map(([key, config]) => (
            <div key={key} className="bg-gray-50 p-3 rounded-lg border border-gray-100">
              <div className="flex items-center mb-1 gap-1.5">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: config.color }}
                ></div>
                <span className="text-sm font-medium text-gray-700">
                  {config.label}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-xl font-semibold">
                    {data[key as keyof typeof data].toFixed(1)}
                    <span className="text-xs font-normal text-gray-500">/10</span>
                  </div>
                  <div className={`text-xs font-medium ${getRatingColor(data[key as keyof typeof data])}`}>
                    {getScoreRating(data[key as keyof typeof data])}
                  </div>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="p-1 text-gray-400 hover:text-gray-600">
                        <HelpCircle className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="p-3 max-w-xs">
                      <p className="text-xs">{config.description}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-3 border-t border-gray-100">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Score Scale Reference</h4>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-red-600 mr-1"></div>
              <span className="text-xs text-gray-600">0-3.4: High Risk</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-amber-500 mr-1"></div>
              <span className="text-xs text-gray-600">3.5-6.4: Moderate</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-emerald-600 mr-1"></div>
              <span className="text-xs text-gray-600">6.5-8.4: Good</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-green-600 mr-1"></div>
              <span className="text-xs text-gray-600">8.5-10: Excellent</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Risk3DRadar;
